/* global globalThis */
import { openOfflineDatabase } from './sqliteDatabase';
// This cache is a local reader, never a queue that automatically retries AI/server writes.
// Android stores compact JSON in private SQLite. The browser adapter is for development
// and has the browser's localStorage quota; failures propagate to the caller/UI.
const PREFIX = 'upya.offline.conversation.v1:';
const DATABASE = 'upya_conversations';
const OMIT = /^(base64|dataUrl|imageBase64|audioBase64|fileBase64|blob|file|rawFile|binary|html|renderedHtml)$/i;

function accountKey(userId) {
  if (userId === undefined || userId === null || String(userId).trim() === '') {
    throw new Error('An account is required for offline conversations');
  }
  return String(userId);
}

// Keep structured finance/source metadata, but never persist file contents or transient URLs.
export function compactConversation(value, ancestors = new Set()) {
  if (typeof value === 'string') {
    return value.replace(/data:[^\s"'<>)]*;base64,[A-Za-z0-9+/=\r\n]+/gi, '[attachment unavailable offline]')
      .replace(/blob:[^\s"'<>)]*/g, '[attachment unavailable offline]');
  }
  if (value === null || typeof value === 'boolean' || typeof value === 'number') return value;
  if (typeof value !== 'object' || ancestors.has(value)) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer ||
      (typeof Blob !== 'undefined' && value instanceof Blob)) return undefined;
  const next = new Set(ancestors).add(value);
  if (Array.isArray(value)) return value.map((item) => compactConversation(item, next)).filter((item) => item !== undefined);
  return Object.fromEntries(Object.entries(value).filter(([key]) => !OMIT.test(key)).map(([key, item]) =>
    [key, compactConversation(item, next)]).filter(([, item]) => item !== undefined));
}

export function createLocalConversationId() {
  return `local:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}

export function createBrowserConversationStore(storage = globalThis.localStorage) {
  const prefix = (account) => `${PREFIX}${encodeURIComponent(account)}:`;
  const key = (account, id) => `${prefix(account)}${encodeURIComponent(id)}`;
  return {
    async list(account) {
      const records = [];
      for (let i = 0; i < storage.length; i += 1) {
        const name = storage.key(i);
        if (name?.startsWith(prefix(account))) records.push(JSON.parse(storage.getItem(name)));
      }
      return records;
    },
    async get(account, id) {
      const raw = storage.getItem(key(account, id));
      return raw ? JSON.parse(raw) : null;
    },
    async put(account, conversation) {
      storage.setItem(key(account, conversation.id), JSON.stringify(conversation));
    },
    async remove(account, id) { storage.removeItem(key(account, id)); },
    async clear(account) {
      const names = [];
      for (let i = 0; i < storage.length; i += 1) {
        const name = storage.key(i);
        if (name?.startsWith(prefix(account))) names.push(name);
      }
      names.forEach((name) => storage.removeItem(name));
    },
  };
}

export async function createSQLiteConversationStore(connection) {
  await connection.execute(`CREATE TABLE IF NOT EXISTS offline_conversations (
    account_id TEXT NOT NULL, conversation_id TEXT NOT NULL, snapshot TEXT NOT NULL,
    PRIMARY KEY (account_id, conversation_id)
  );`);
  return {
    async list(account) {
      const result = await connection.query('SELECT snapshot FROM offline_conversations WHERE account_id = ?', [account]);
      return (result.values || []).map((row) => JSON.parse(row.snapshot));
    },
    async get(account, id) {
      const result = await connection.query('SELECT snapshot FROM offline_conversations WHERE account_id = ? AND conversation_id = ?', [account, String(id)]);
      return result.values?.[0] ? JSON.parse(result.values[0].snapshot) : null;
    },
    async put(account, conversation) {
      await connection.run('INSERT OR REPLACE INTO offline_conversations (account_id, conversation_id, snapshot) VALUES (?, ?, ?)',
        [account, String(conversation.id), JSON.stringify(conversation)]);
    },
    async remove(account, id) {
      await connection.run('DELETE FROM offline_conversations WHERE account_id = ? AND conversation_id = ?', [account, String(id)]);
    },
    async clear(account) {
      await connection.run('DELETE FROM offline_conversations WHERE account_id = ?', [account]);
    },
  };
}

async function platformStore() {
  const { Capacitor } = await import('@capacitor/core');
  if (Capacitor.getPlatform() !== 'android') return createBrowserConversationStore();
  return createSQLiteConversationStore(await openOfflineDatabase(DATABASE));
}

export function createConversationRepository(getStore = platformStore) {
  let storePromise;
  let queue = Promise.resolve();
  const listeners = new Set();
  const store = () => {
    if (!storePromise) storePromise = Promise.resolve().then(getStore).catch((error) => { storePromise = undefined; throw error; });
    return storePromise;
  };
  const serialize = (operation) => {
    const result = queue.then(operation);
    queue = result.catch(() => {});
    return result;
  };
  const emit = (userId, id) => listeners.forEach((listener) => {
    // UI subscribers must not turn a committed write into an apparent storage failure.
    try { listener({ userId, id }); } catch (_) { /* observer failure */ }
  });
  return {
    listConversations(userId, { projectId, scope } = {}) {
      return serialize(async () => {
        const rows = await (await store()).list(accountKey(userId));
        return rows.filter((row) => projectId ? String(row.projectId) === String(projectId) : scope !== 'general' || !row.projectId)
          .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      });
    },
    saveConversationSnapshot(userId, conversation, { source = 'local', force = false } = {}) {
      return serialize(async () => {
        const account = accountKey(userId);
        if (conversation?.id === undefined || conversation.id === null || conversation.id === '') {
          throw new Error('A stable conversation id is required');
        }
        if (!Array.isArray(conversation.messages)) throw new Error('Only downloaded conversation content can be cached');
        const driver = await store();
        const previous = await driver.get(account, conversation.id);
        if (source === 'server' && !force && previous?.pendingLocal) return previous;
        const snapshot = {
          ...compactConversation(conversation),
          offlineAccountId: account,
          cachedAt: new Date().toISOString(),
          offlineAvailable: true,
          pendingLocal: source === 'server' ? false : conversation.pendingLocal ?? true,
        };
        await driver.put(account, snapshot);
        emit(account, snapshot.id);
        return snapshot;
      });
    },
    removeConversationSnapshot(userId, id) {
      return serialize(async () => {
        const account = accountKey(userId);
        await (await store()).remove(account, id);
        emit(account, id);
      });
    },
    clearAccountConversations(userId) {
      return serialize(async () => {
        const account = accountKey(userId);
        await (await store()).clear(account);
        emit(account, null);
      });
    },
    // Only call with an authoritative COMPLETE server list for exactly this scope.
    // A failed request, a metadata page or a domain-filtered list is not authoritative.
    reconcileServerConversations(userId, serverIds, { projectId, scope } = {}) {
      return serialize(async () => {
        const account = accountKey(userId);
        if (!Array.isArray(serverIds) || serverIds.some((id) => id === null || id === undefined)) {
          throw new Error('An authoritative array of server conversation ids is required');
        }
        const knownIds = new Set(serverIds.map(String));
        const driver = await store();
        const rows = await driver.list(account);
        const removedIds = [];
        for (const row of rows) {
          const inScope = projectId ? String(row.projectId) === String(projectId) : scope !== 'general' || !row.projectId;
          if (!inScope || row.pendingLocal || row.localOnly || knownIds.has(String(row.id))) continue;
          await driver.remove(account, row.id);
          removedIds.push(row.id);
          emit(account, row.id);
        }
        return removedIds;
      });
    },
    async hasOfflineConversations(userId) { return (await this.listConversations(userId)).length > 0; },
    subscribeToConversations(listener) { listeners.add(listener); return () => listeners.delete(listener); },
  };
}

const repository = createConversationRepository();
export const listConversations = (...args) => repository.listConversations(...args);
export const saveConversationSnapshot = (...args) => repository.saveConversationSnapshot(...args);
export const removeConversationSnapshot = (...args) => repository.removeConversationSnapshot(...args);
export const clearAccountConversations = (...args) => repository.clearAccountConversations(...args);
export const clearAccount = clearAccountConversations;
export const reconcileServerConversations = (...args) => repository.reconcileServerConversations(...args);
export const hasOfflineConversations = (...args) => repository.hasOfflineConversations(...args);
export const subscribeToConversations = (...args) => repository.subscribeToConversations(...args);
